import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Progress,
  Typography,
  Upload,
  Form,
  Select,
  Space,
  Spin,
  Alert,
} from "antd";
import { PageHeader } from "@ant-design/pro-components";
import { CheckCircle, Play, RefreshCw } from "lucide-react";
import { notify } from "@/components/common/Notification";
import CustomButton from "@/components/common/Button";
import {
  DataOperationsService,
  UploadService,
  SchemaService,
} from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import { filterByLabel } from "@/lib/filterByLabel";
import { ColumnMapping } from "@/utils/types/dataOperator";
import PTSNavigationRoutes from "../../utils/navigationRoutes";

const { Text } = Typography;

const SCHEMA_NAME = "PTS Emp";
const ENTITY_NAME = "pts_emp";

const ImportEmployeePage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(1); // 1: Upload, 2: Mapping
  const [isUploading, setIsUploading] = useState(false);
  const [isUpdatingMapping, setIsUpdatingMapping] = useState(false);
  const [isRunningImport, setIsRunningImport] = useState(false);
  const [isMappingSaved, setIsMappingSaved] = useState(false);
  const [importId, setImportId] = useState<string | null>(null);
  const [fileColumns, setFileColumns] = useState<string[]>([]);
  const [mappingError, setMappingError] = useState<string>("");

  // Fetch column grid data for mapping
  const { data: columnGridData, isLoading: isLoadingColumns } = useQuery({
    queryKey: ["columnGridData", SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(SCHEMA_NAME),
    enabled: currentStep >= 2,
  });

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    // Read CSV headers
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const lines = text.split("\n");
      if (lines.length > 0) {
        const headers = lines[0]
          .split(",")
          .map(h => h.trim().replace(/"/g, ""));
        setFileColumns(headers);
      }
    };
    reader.readAsText(file);

    // Upload file using /api/file-utility/files/
    setIsUploading(true);
    try {
      const response = await UploadService.uploadFiles({
        folderName: `importfiles`,
        files: [file],
      });

      let docId;
      if (response?.[0]?.id) {
        docId = response[0].id;
      } else {
        notify.error("Upload Failed", "No document ID received from server.");
        setIsUploading(false);
        return;
      }

      // Create import using /api/import-data/create/pts_emp/
      const importResponse = await DataOperationsService.importData(
        ENTITY_NAME,
        {
          document_id: docId,
          import_type: "create new records",
        }
      );

      if (importResponse?.import_id) {
        setImportId(importResponse.import_id);
        setCurrentStep(2);
        // notify.success("File Uploaded", "File has been uploaded successfully.");
      } else {
        notify.error(
          "Import Setup Failed",
          "No import ID received from server."
        );
      }
    } catch (error: any) {
      notify.error("Upload Failed", error?.message ?? "Failed to upload file.");
    } finally {
      setIsUploading(false);
    }
  };

  // Get database column names for mapping (filter out system fields)
  const dbColumns =
    columnGridData?.grid
      ?.filter((field: any) => {
        const systemFields = [
          "id",
          "is_active",
          "created_on",
          "updated_on",
          "created_by",
          "updated_by",
          "tenant",
          "created_by_id",
          "updated_by_id",
          "tenant_id",
        ];
        return !systemFields.includes(field.field_name.toLowerCase());
      })
      ?.map((field: any) => ({
        label: field.field_title,
        value: field.field_name.toLowerCase(),
      })) ?? [];

  // Filter file columns to exclude system fields from mapping
  const getMappableFileColumns = () => {
    const systemFields = [
      "id",
      "is_active",
      "created_on",
      "updated_on",
      "created_by",
      "updated_by",
      "tenant",
      "created_by_id",
      "updated_by_id",
      "tenant_id",
    ];
    return fileColumns.filter(
      column => !systemFields.includes(column.toLowerCase())
    );
  };

  // Handle column mapping update
  const handleUpdateMapping = async (
    importId: string,
    mapping: ColumnMapping[]
  ) => {
    setIsUpdatingMapping(true);
    try {
      const response = await DataOperationsService.updateColumnMapping(
        importId,
        {
          column_mapping: mapping,
        }
      );
      if (response) {
        setIsMappingSaved(true);
        // notify.success("Mapping Saved", "Column mapping has been saved successfully.");
      }
    } catch (error: any) {
      notify.error(
        "Mapping Failed",
        error?.message ?? "Failed to save column mapping."
      );
    } finally {
      setIsUpdatingMapping(false);
    }
  };

  // Handle column mapping form submission
  const handleColumnMapping = async (values: any) => {
    setMappingError("");

    // Filter out empty values and create mapping
    const mapping: ColumnMapping[] = Object.entries(values)
      .filter(([, value]) => value && value !== "")
      .map(([key, value]) => ({
        file_column_name: value as string,
        db_column_name: key,
      }));

    if (mapping.length === 0) {
      setMappingError("Please map at least one column before proceeding.");
      notify.warning(
        "No Mappings",
        "Please map at least one column before proceeding."
      );
      return;
    }

    // Check for duplicate file_column_name
    const fileNames = mapping.map(m => m.file_column_name);
    const hasDuplicates = fileNames.length !== new Set(fileNames).size;
    if (hasDuplicates) {
      setMappingError(
        "Each file column can only be mapped to one database field."
      );
      return;
    }

    if (importId) {
      await handleUpdateMapping(importId, mapping);
    } else {
      setMappingError("No import ID available. Please upload a file first.");
    }
  };

  // Handle reset mapping
  const handleResetMapping = () => {
    form.resetFields();
    setMappingError("");
    setIsMappingSaved(false);
    notify.success("Mapping Reset", "Column mapping has been reset.");
  };

  // Handle run import
  const handleRunImport = async () => {
    if (!importId) {
      notify.error(
        "No Import ID",
        "Please upload a file and save mapping first."
      );
      return;
    }

    setIsRunningImport(true);
    try {
      const response = await DataOperationsService.runImport(importId, {
        is_pts_emp: true,
      });
      if (response) {
        // notify.success("Import Started", "Import process has been started successfully.");
        setTimeout(() => {
          navigate(PTSNavigationRoutes.EMPLOYEE);
        }, 2000);
      }
    } catch (error: any) {
      notify.error(
        "Import Failed",
        error?.message ?? "Failed to start import process."
      );
    } finally {
      setIsRunningImport(false);
    }
  };

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title="Import Employee"
          onBack={() => navigate(PTSNavigationRoutes.EMPLOYEE)}
        />
      }
    >
      <div className="space-y-8">
        {/* Step 1: File Upload */}
        <div className="space-y-4">
          <Text strong className="text-lg">
            Step 1: Upload File
          </Text>
          <Upload.Dragger
            accept=".csv"
            beforeUpload={file => {
              handleFileUpload(file);
              return false;
            }}
            showUploadList={false}
            disabled={isUploading || currentStep > 1}
          >
            <div className="flex flex-col gap-4">
              {currentStep > 1 ? (
                <>
                  <CheckCircle className="mx-auto text-green-500" size={24} />
                  <p className="font-medium text-green-600">
                    File Upload Completed
                  </p>
                  <p className="text-sm text-gray-500">
                    File has been successfully uploaded and processed
                  </p>
                </>
              ) : (
                <>
                  <p>Click or drag CSV file to this area to upload</p>
                  <p className="text-sm text-gray-500">
                    Support for CSV files only
                  </p>
                </>
              )}
            </div>
            {isUploading && (
              <div className="mt-4">
                <Progress percent={75} status="active" strokeColor="#1890ff" />
                <Text type="secondary" className="text-sm">
                  Processing file...
                </Text>
              </div>
            )}
          </Upload.Dragger>
        </div>

        {/* Step 2: Column Mapping */}
        {currentStep >= 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Text strong className="text-lg">
                Step 2: Column Mapping
              </Text>
              <CustomButton
                size="small"
                onClick={handleResetMapping}
                icon={<RefreshCw size={14} />}
                disabled={isUpdatingMapping || isRunningImport}
                title="Reset Column Mapping"
              >
                Reset
              </CustomButton>
            </div>

            {isLoadingColumns ? (
              <div className="py-8 text-center">
                <Spin size="large" />
                <Text className="mt-4 block">Loading database schema...</Text>
              </div>
            ) : dbColumns.length === 0 ? (
              <Alert
                type="error"
                message="No database fields available for mapping"
                description="Please check your schema configuration."
                showIcon
              />
            ) : (
              <Form
                form={form}
                onFinish={handleColumnMapping}
                layout="vertical"
                className="space-y-4"
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {dbColumns.map(
                    (dbColumn: { label: string; value: string }) => (
                      <Card key={dbColumn.value}>
                        <Form.Item
                          label={
                            <Space>
                              <Text strong>{dbColumn.label}</Text>
                              <Text type="secondary" className="text-xs">
                                ({dbColumn.value})
                              </Text>
                            </Space>
                          }
                          name={dbColumn.value}
                        >
                          <Select
                            placeholder="Select file column"
                            allowClear
                            options={getMappableFileColumns().map(
                              fileColumn => {
                                const currentValues = form.getFieldsValue();
                                const isDisabled = Object.entries(
                                  currentValues
                                ).some(
                                  ([key, val]) =>
                                    key !== dbColumn.value && val === fileColumn
                                );
                                return {
                                  label: fileColumn,
                                  value: fileColumn,
                                  disabled: isDisabled,
                                };
                              }
                            )}
                            showSearch
                            filterOption={filterByLabel}
                          />
                        </Form.Item>
                      </Card>
                    )
                  )}
                </div>

                {mappingError && (
                  <Alert
                    type="error"
                    message={mappingError}
                    showIcon
                    className="mb-4"
                  />
                )}

                {fileColumns.length > getMappableFileColumns().length && (
                  <Alert
                    type="info"
                    message="System fields are automatically handled"
                    description="System fields (id, is_active, created_on, etc.) are automatically handled during import and don't need to be mapped."
                    showIcon
                    className="mb-4"
                  />
                )}

                <div className="space-y-4">
                  <CustomButton
                    type="primary"
                    loading={isUpdatingMapping}
                    disabled={isRunningImport}
                    onClick={() => {
                      const values = form.getFieldsValue();
                      const hasValues = Object.values(values).some(
                        val => val && val !== ""
                      );

                      if (!hasValues) {
                        notify.warning(
                          "No Mappings",
                          "Please map at least one column before proceeding."
                        );
                        return;
                      }

                      handleColumnMapping(values);
                    }}
                    title="Save Column Mapping"
                  >
                    {isUpdatingMapping ? "Updating..." : "Update Mapping"}
                  </CustomButton>

                  {isMappingSaved && (
                    <CustomButton
                      type="primary"
                      loading={isRunningImport}
                      disabled={isUpdatingMapping}
                      onClick={handleRunImport}
                      icon={<Play size={14} />}
                      size="large"
                      className="w-full"
                      title="Execute Import Process"
                    >
                      {isRunningImport ? "Importing..." : "Start Import"}
                    </CustomButton>
                  )}
                </div>
              </Form>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default ImportEmployeePage;
